import { type PackagingInformationEntry, isKITKITProduct } from "~/domains/product/types/ProductData";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { EffectivePackagingEntry } from "~/domains/product/types/EffectivePackaging";
import type { PredecessorProduct } from "~/domains/product/types/PackagingOption";
import { ImageFormat } from "~/domains/product/types/ProductDataDTO";
import { getPrimaryImage } from "~/utils/product/getPrimaryImage";
import { canSeeCountryOrigin } from "~/utils/siteModes";
import { getInStockAndInProduction } from "~/utils/packageEntryUtils";

export interface PredecessorDescriptorWithEntries {
  descriptor: ProductDescriptor;
  entries: PackagingInformationEntry[];
}

type FifteenDigitKey = string;
type LookupTuple = [ProductDescriptor, PackagingInformationEntry];

interface PredecessorEntry {
  materialNo: string;
  predDescriptor: ProductDescriptor;
  predEntry: PackagingInformationEntry;
  totalStock: number;
}

function buildLookup(predecessors: PredecessorDescriptorWithEntries[]) {
  const lookup = new Map<FifteenDigitKey, LookupTuple>();

  for (const { descriptor: predDescriptor, entries } of predecessors) {
    const sapMaterialNumber = predDescriptor.base.sapMaterialNumber;

    for (const predEntry of entries) {
      lookup.set(sapMaterialNumber + predEntry.packCode, [predDescriptor, predEntry]);
    }
  }

  return lookup;
}

function buildPredecessorEntries(
  descriptor: ProductDescriptor,
  entry: PackagingInformationEntry,
  lookup: Map<FifteenDigitKey, LookupTuple>,
) {
  const fifteenDigitEntry = descriptor.base.fifteenDigit?.[entry.packCode];
  const predecessorMaterialNos = fifteenDigitEntry?.predecessors ?? [];

  return predecessorMaterialNos
    .flatMap((materialNo) => {
      const predecessorMatch = lookup.get(materialNo);

      if (!predecessorMatch) {
        return [];
      }

      const [predDescriptor, predEntry] = predecessorMatch;

      return {
        materialNo,
        predDescriptor,
        predEntry,
        totalStock: getInStockAndInProduction(predEntry),
      };
    })
    .filter((entry) => entry.totalStock > 0)
    .sort((left, right) => right.totalStock - left.totalStock);
}

function buildBaseEntry(descriptor: ProductDescriptor, entry: PackagingInformationEntry): EffectivePackagingEntry {
  const isKitKit = isKITKITProduct(descriptor.base);
  const fifteenDigitEntry = descriptor.base.fifteenDigit?.[entry.packCode];

  return {
    packCode: entry.packCode,
    rawPack: entry,
    packSize: entry.packSize,
    ean: isKitKit ? null : fifteenDigitEntry?.schaefflerEan ? [fifteenDigitEntry.schaefflerEan] : [],
    aliases: isKitKit ? null : descriptor.base.packageAliases?.[entry.packCode] ?? [],
    countriesOfOrigin: isKitKit
      ? null
      : !canSeeCountryOrigin()
      ? []
      : fifteenDigitEntry?.countryOfOrigin
      ? [fifteenDigitEntry.countryOfOrigin]
      : [],
    predecessors: [],
    hasMixedPackSizes: false,
  };
}

function aggregateUniqueValues(baseEntry: EffectivePackagingEntry, predecessorEntries: PredecessorEntry[]) {
  const eans = baseEntry.ean ? new Set(baseEntry.ean) : null;
  const aliases = baseEntry.aliases ? new Set(baseEntry.aliases) : null;
  const countriesOfOrigin = baseEntry.countriesOfOrigin ? new Set(baseEntry.countriesOfOrigin) : null;

  for (const { predDescriptor, predEntry } of predecessorEntries) {
    const digitEntry = predDescriptor.base.fifteenDigit?.[predEntry.packCode];

    if (digitEntry?.schaefflerEan) {
      eans?.add(digitEntry.schaefflerEan);
    }
    predDescriptor.base.packageAliases?.[predEntry.packCode]?.forEach((alias) => aliases?.add(alias));
    if (canSeeCountryOrigin() && digitEntry?.countryOfOrigin) {
      countriesOfOrigin?.add(digitEntry.countryOfOrigin);
    }
  }

  return {
    ean: eans ? Array.from(eans) : null,
    aliases: aliases ? Array.from(aliases) : null,
    countriesOfOrigin: countriesOfOrigin ? Array.from(countriesOfOrigin) : null,
  };
}

function buildPredecessorProducts(
  descriptor: ProductDescriptor,
  predecessorEntries: PredecessorEntry[],
): PredecessorProduct[] {
  const predecessorProducts: PredecessorProduct[] = [];

  for (const { materialNo, predDescriptor } of predecessorEntries) {
    if (materialNo.slice(0, 13) === descriptor.base.sapMaterialNumber) {
      continue;
    }

    const primaryImage = getPrimaryImage(predDescriptor.base.images, { imageFormat: ImageFormat.thumbnail });

    predecessorProducts.push({
      imageUrl: primaryImage?.url ?? "",
      url: predDescriptor.base.url,
      designation: predDescriptor.base.designation,
      sapMaterialNo: materialNo,
    });
  }

  return predecessorProducts;
}

export function buildSimpleEffectivePackagingEntry(
  descriptor: ProductDescriptor,
  entry: PackagingInformationEntry,
): EffectivePackagingEntry {
  return buildBaseEntry(descriptor, entry);
}

export function buildEffectivePackaging(
  entries: PackagingInformationEntry[],
  descriptor: ProductDescriptor,
  predecessors: PredecessorDescriptorWithEntries[],
): EffectivePackagingEntry[] {
  const lookup = buildLookup(predecessors);

  return entries.flatMap((entry) => {
    const fifteenDigitEntry = descriptor.base.fifteenDigit?.[entry.packCode];
    const successorMaterialNo = fifteenDigitEntry?.successor;

    if (successorMaterialNo && successorMaterialNo.slice(0, 13) === descriptor.base.sapMaterialNumber) {
      return [];
    }

    const baseEntry = buildBaseEntry(descriptor, entry);
    const predecessorEntries = buildPredecessorEntries(descriptor, entry, lookup);

    if (predecessorEntries.length === 0) {
      return [baseEntry];
    }

    const hasMixedPackSizes =
      new Set([entry.packSize, ...predecessorEntries.map((item) => item.predEntry.packSize)]).size > 1;

    const smallestPackSize = Math.min(...predecessorEntries.map((item) => item.predEntry.packSize));

    return [
      {
        ...baseEntry,
        ...aggregateUniqueValues(baseEntry, predecessorEntries),
        predecessors: buildPredecessorProducts(descriptor, predecessorEntries),
        hasMixedPackSizes,
        packSize: smallestPackSize ?? entry.packSize,
      },
    ];
  });
}
